import type { ReactNode } from "react";
import { useBrand } from "@fprint/ui";
import { Link } from "react-router-dom";

/**
 * Routed placeholder pages for the legal + contact surface (COMP-2).
 *
 * These are SCAFFOLDING ONLY — deliberately empty of binding legal text. Each
 * renders a prominent placeholder notice so the operator (not this code) must
 * supply the real Terms / Privacy / Refund & Returns / Contact content before
 * go-live. Do NOT add legal clauses here.
 */
function LegalPage({
  title,
  kind,
  testid,
  children,
}: {
  title: string;
  /** Human name of the policy, used verbatim in the placeholder notice. */
  kind: string;
  testid: string;
  children?: ReactNode;
}) {
  const brand = useBrand();
  return (
    <main
      className="mx-auto flex min-h-screen max-w-2xl flex-col gap-6 px-6 py-12"
      data-testid={testid}
    >
      <header>
        <h1 className="text-3xl font-bold">{title}</h1>
      </header>

      <div
        role="note"
        className="rounded-md border border-warning bg-warning/10 px-4 py-3 text-sm text-warning-fg"
        data-testid="legal-placeholder-notice"
      >
        ⚠️ PLACEHOLDER — the operator must replace this with real {kind} content
        before go-live.
      </div>

      {children}

      <nav className="mt-2 text-sm">
        <Link to="/" className="text-primary underline">
          Back to {brand.name}
        </Link>
      </nav>
    </main>
  );
}

export function TermsPage() {
  return (
    <LegalPage
      title="Terms of Service"
      kind="Terms of Service"
      testid="page-terms"
    />
  );
}

export function PrivacyPage() {
  return (
    <LegalPage
      title="Privacy Policy"
      kind="Privacy Policy"
      testid="page-privacy"
    />
  );
}

export function RefundsPage() {
  return (
    <LegalPage
      title="Refund & Returns Policy"
      kind="Refund & Returns Policy"
      testid="page-refunds"
    />
  );
}

export function ContactPage() {
  const brand = useBrand();
  return (
    <LegalPage title="Contact" kind="Contact" testid="page-contact">
      <p className="text-sm text-muted-fg">
        Questions or a problem with an order? Email{" "}
        <a
          href={`mailto:${brand.supportEmail}`}
          className="text-primary underline"
          data-testid="contact-support-email"
        >
          {brand.supportEmail}
        </a>
        .
      </p>
    </LegalPage>
  );
}
